import { AppConfiguration } from "config";
import { useEffect, useMemo } from "react";
import { MetaTags } from "react-meta-tags";
import { useParams } from "react-router-dom";
import { Card, CardBody, Col, Container, Row } from "reactstrap";
import ToolkitProvider from 'react-bootstrap-table2-toolkit';
import BootstrapTable from "react-bootstrap-table-next";
import GenericBreadcrumb from "components/Common/GenericBreadcrumb";
import { REPORTING_TIMEZONE, REQUIRED_FEATURE_FLAGS } from "helpers/sf_constants";
import { utcToZonedTime } from "date-fns-tz";
import { format } from "date-fns";
import { getScheduledReportLogsById, redirectToDownloadUrl, resetLastScheduledReportsAction } from "store/scheduled_reports/actions";
import { useDispatch, useSelector } from "react-redux";
import filterFactory from 'react-bootstrap-table2-filter';
import useFeatureFlags from "hooks/useFeatureFlags";
import { customSortCaret } from "helpers/bootstrap_table_helper";

const ScheduledReportLog = () => {
  const { scheduled_report_id } = useParams();
  const [isFeatureFlagActive] = useFeatureFlags();
  const dispatch = useDispatch();
  const {
    scheduled_report_logs,
    redirect_to_download_url,
    redirect_to_download_url_loading,
    redirect_to_download_url_error
  } = useSelector((state) => ({
    scheduled_report_logs: state.ScheduledReports.scheduled_report_logs,
    redirect_to_download_url: state.ScheduledReports.redirect_to_download_url,
    redirect_to_download_url_loading: state.ScheduledReports.redirect_to_download_url_loading,
    redirect_to_download_url_error: state.ScheduledReports.redirect_to_download_url_error,
  }));

  useEffect(() => {
    const queryParams = { scheduled_report_id }
    dispatch(getScheduledReportLogsById({ queryParams }, scheduled_report_id))
  }, [scheduled_report_id])

  const tableData = useMemo(() => {
    const current_report_logs = scheduled_report_logs[scheduled_report_id.toString()]?.logs || [];
    return current_report_logs;
  }, [scheduled_report_logs])

  const reportTitle = useMemo(() => {
    return scheduled_report_logs[scheduled_report_id.toString()]?.title
  })

  const handleScheduledReportLogDownload = (log_id) => {
    dispatch(redirectToDownloadUrl({ id: log_id }))
  };

  useEffect(() => {
    if (redirect_to_download_url?.signed_url) {
      window.open(redirect_to_download_url?.signed_url, '_blank');
      dispatch(resetLastScheduledReportsAction())
    }
  }, [redirect_to_download_url?.signed_url])


  const columns = [
    {
      dataField: 'created_at',
      text: 'Time',
      sort: true,
      formatter: (cell) => format(utcToZonedTime(cell, REPORTING_TIMEZONE), 'MM/dd/yyyy hh:mm:ss aa'),
    },
    {
      dataField: 'log_level',
      text: 'Log Level',
      sort: true,
    },
    {
      dataField: 'log_entry',
      text: 'Description',
    },
    {
      dataField: 'id',
      text: 'Download',
      hidden: !isFeatureFlagActive(REQUIRED_FEATURE_FLAGS.SF_FEATURE_FLAG_SHOW_DOWNLOAD_SCHEDULED_REPORT_COLUMN),
      formatter: (cell, row) => {
        if (!row?.scheduled_report_upload_id) {
          return <div>
            --
          </div>;
        }
        return <i className="bx bx-download text-sfblue" onClick={() => handleScheduledReportLogDownload(row?.scheduled_report_upload_id)}></i>
      }
    },
  ]

  const defaultSorted = [
    {
      dataField: 'created_at',
      order: 'desc'
    }
  ]

  return (
    <div className="page-content" id="page-content-wrapper">
      <MetaTags>
        <title>Scheduled Report Logs | {AppConfiguration.WEBSITE}</title>
      </MetaTags>
      <Container fluid>
        <GenericBreadcrumb
          breadcrumbItem={`Scheduled Report Logs${reportTitle ? `: ${reportTitle}` : ''}`}
          backOption={true}
          title="Scheduled Reports"
          backLink={'/analytics/scheduled_reports'} />
        <Row className="mt-3">
          <Col className="col-12">
            <Card>
              <CardBody>
                <ToolkitProvider
                  keyField="id"
                  columns={columns}
                  data={tableData}
                >
                  {(toolkitProps) => (
                    <div className="table-responsive align-top height-table">
                      <BootstrapTable
                        keyField="id"
                        responsive
                        bordered={false}
                        striped={false}
                        defaultSorted={defaultSorted}
                        rowClasses="tr-hover"
                        filter={filterFactory()}
                        classes="table align-middle table-nowrap text-black"
                        headerClasses='text-black'
                        headerWrapperClasses="thead-light"
                        sort={{ sortCaret: customSortCaret }}
                        {...toolkitProps.baseProps}
                        noDataIndication="You currently don't have any scheduled report logs"
                      />
                    </div>
                  )}

                </ToolkitProvider>
              </CardBody>
            </Card>
          </Col>
        </Row>
        {/* Data table ends */}
      </Container>
    </div>
  )
}

export default ScheduledReportLog;
